/**
 * /campaigns/:id — campaign detail layout with tabs.
 *
 * Loads the campaign + recent jobs once on mount; child routes
 * (Brief, Assets, Jobs view) read the campaign via `useOutletContext`
 * and run their own scoped fetches.
 *
 * Each child route corresponds to a tab:
 *   /campaigns/:id          ← Brief (default)
 *   /campaigns/:id/assets   ← Assets library
 *   /campaigns/:id/jobs/:jobId ← Variants + render for a specific job
 *
 * Brand context (name, product, audience, voice, model, colors)
 * lives on the campaign row — children inherit it. Submitting a
 * brief from the Brief tab creates a new job inside this campaign;
 * uploads on the Assets tab attach to this campaign's library.
 */

import { useEffect, useMemo } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { useState } from 'react';
import { apiFetch, apiJson } from '../lib/api';

export interface Campaign {
  id: string;
  name: string;
  product: string;
  audience: string;
  offer: string;
  proof: string;
  default_cta: string;
  brand_settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Job {
  id: string;
  platform: string;
  tone: string;
  duration_seconds: number;
  status: string;
  selected_variant_id: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignContext {
  campaign: Campaign;
  jobs: Job[];
  assetCount: number;
  refreshJobs: () => Promise<void>;
}

export function useCampaignContext(): CampaignContext {
  return useOutletContext<CampaignContext>();
}

export function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [assetCount, setAssetCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!campaign) return;
    const ok = window.confirm(
      `Delete "${campaign.name}"?\n\n` +
        `Permanently removes the campaign + ${jobs.length} job${jobs.length === 1 ? '' : 's'}` +
        ` + ${assetCount} asset${assetCount === 1 ? '' : 's'} + all rendered MP4s and ` +
        'voiceovers. Cannot be undone.',
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/marketing/campaigns/${campaign.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${body || 'delete_failed'}`);
      }
      navigate('/campaigns', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
    }
  }

  async function load() {
    if (!id) return;
    try {
      const res = await apiJson<{ campaign: Campaign; jobs: Job[]; asset_count: number }>(
        `/api/marketing/campaigns/${id}`,
      );
      setCampaign(res.campaign);
      setJobs(res.jobs);
      setAssetCount(res.asset_count);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaign');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const context: CampaignContext | null = useMemo(
    () =>
      campaign
        ? {
            campaign,
            jobs,
            assetCount,
            refreshJobs: async () => {
              await load();
            },
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaign, jobs, assetCount],
  );

  if (error) {
    return (
      <div className="marketing-page">
        <div className="alert alert-error">{error}</div>
        <Link to="/campaigns" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}>
          ← Back to campaigns
        </Link>
      </div>
    );
  }
  if (!campaign || !context) {
    return (
      <div className="marketing-page" style={{ paddingTop: 64, textAlign: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  const brand = campaign.brand_settings ?? {};
  const voiceId = typeof brand.voice_id === 'string' ? brand.voice_id : null;
  const model = typeof brand.model === 'string' ? brand.model : null;

  return (
    <div className="marketing-page">
      <header className="marketing-header">
        <div>
          <Link to="/campaigns" style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            ← Campaigns
          </Link>
          <h1 style={{ marginTop: 4 }}>{campaign.name}</h1>
          <p>{campaign.product}</p>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            fontSize: 12,
            color: 'var(--color-text-muted)',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {voiceId && (
            <span style={{ background: 'var(--color-surface-alt)', padding: '4px 8px', borderRadius: 6 }}>
              voice: {voiceId.slice(0, 12)}{voiceId.length > 12 ? '…' : ''}
            </span>
          )}
          {model && (
            <span style={{ background: 'var(--color-surface-alt)', padding: '4px 8px', borderRadius: 6 }}>
              {model}
            </span>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={deleting}
            onClick={handleDelete}
            title="Delete campaign and all associated jobs, assets, voiceovers, and renders"
            style={{ color: 'crimson' }}
          >
            {deleting ? 'Deleting…' : 'Delete campaign'}
          </button>
        </div>
      </header>

      <nav
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--color-border, rgba(255,255,255,0.1))',
          marginTop: 16,
          marginBottom: 24,
        }}
      >
        <TabLink to={`/campaigns/${campaign.id}/brief`}>Brief</TabLink>
        <TabLink to={`/campaigns/${campaign.id}/assets`}>
          Assets <span style={{ opacity: 0.6 }}>({assetCount})</span>
        </TabLink>
        <TabLink to={`/campaigns/${campaign.id}/higgsfield`}>Higgsfield</TabLink>
        <TabLink to={`/campaigns/${campaign.id}/jobs`}>
          Jobs <span style={{ opacity: 0.6 }}>({jobs.length})</span>
        </TabLink>
      </nav>

      <Outlet context={context satisfies CampaignContext} />
    </div>
  );
}

function TabLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        padding: '8px 16px',
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: isActive ? 600 : 500,
        color: isActive ? 'var(--color-text)' : 'var(--color-text-muted)',
        borderBottom: isActive
          ? '2px solid var(--color-primary, currentColor)'
          : '2px solid transparent',
        marginBottom: -1,
      })}
    >
      {children}
    </NavLink>
  );
}
